import type { QueryClient } from '@tanstack/react-query';

/**
 * Broadcast a provider-agnostic account disconnect and invalidate the shared
 * cross-module account caches, so a disconnect from ANY surface clears stale
 * rows everywhere (#2722). Used by both the IntegrationAccountsModal and the
 * calendar disconnect hook (`useCalendarSync`) — previously the same
 * dispatch + three-invalidation block was copy-pasted in both.
 *
 * The drive module-enabled hook is keyed separately, so it must be invalidated
 * explicitly for the Drive tab strip to drop just-disconnected accounts.
 *
 * NOTE: `'google-accounts'` is the existing shared accounts-query key — kept
 * provider-named per #3999 (renaming it is a separate cache-key migration).
 * The `integrationAccountDisconnected` event is typed via the global
 * `WindowEventMap` augmentation in `types/integration-events.ts`.
 */
export function broadcastIntegrationDisconnect(
  queryClient: QueryClient,
  accountId: string,
): void {
  window.dispatchEvent(
    new CustomEvent('integrationAccountDisconnected', { detail: { accountId } }),
  );
  queryClient.invalidateQueries({ queryKey: ['google-accounts'] });
  queryClient.invalidateQueries({ queryKey: ['gdriveBrowse', 'moduleEnabled'] });
  queryClient.invalidateQueries({ queryKey: ['drive-accounts'] });
}
